/**
 * Session Manager - Centralized session storage and management
 * Handles localStorage-based session persistence with full context
 */

const SESSION_TTL_DAYS = 7; // Sessions expire after 7 days

/**
 * Get storage key for company
 */
const getStorageKey = (companyIdSlug) => {
  return `mentormind_sessions_${companyIdSlug || 'default'}`;
};

/**
 * Generate session ID
 */
const generateSessionId = () => {
  return `session-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
};

/**
 * Generate session title from first message or framework
 */
const generateSessionTitle = (firstMessage, frameworkName) => {
  if (frameworkName) {
    return `${frameworkName} Session`;
  }
  if (firstMessage) {
    // Extract key phrase from first message (first 50 chars)
    const text = firstMessage.content || firstMessage.text || '';
    if (text.length > 0) {
      const preview = text.substring(0, 50).trim();
      if (preview.length > 0) {
        // Capitalize first letter
        return preview.charAt(0).toUpperCase() + preview.slice(1);
      }
    }
  }
  // Fallback to date-based title
  const date = new Date();
  return `Session ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
};

/**
 * Get all sessions from storage
 */
export const getAllSessions = (companyIdSlug) => {
  try {
    const key = getStorageKey(companyIdSlug);
    const data = localStorage.getItem(key);
    if (!data) return { sessions: [], currentSessionId: null };
    const parsed = JSON.parse(data);
    
    // Filter expired sessions (7-day TTL)
    const now = Date.now();
    const allSessions = parsed.sessions || [];
    const validSessions = allSessions.filter(s => {
      if (!s.expiresAt) return true; // Legacy sessions without expiry
      return s.expiresAt > now;
    });
    
    // If sessions were filtered, save the cleaned list
    if (validSessions.length !== allSessions.length) {
      try {
        localStorage.setItem(key, JSON.stringify({
          sessions: validSessions,
          currentSessionId: parsed.currentSessionId || null
        }));
      } catch (e) {
        // Failed to save filtered sessions
      }
    }
    
    return {
      sessions: validSessions,
      currentSessionId: parsed.currentSessionId || null
    };
  } catch (e) {
    console.error('[SessionManager] Error getting sessions:', e);
    return { sessions: [], currentSessionId: null };
  }
};

/**
 * Create a new session
 */
export const createSession = (companyIdSlug, metadata = {}) => {
  const sessionId = generateSessionId();
  const now = Date.now();
  
  const session = {
    id: sessionId,
    title: metadata.title || generateSessionTitle(metadata.firstMessage, metadata.frameworkName),
    createdAt: now,
    updatedAt: now,
    expiresAt: now + (SESSION_TTL_DAYS * 24 * 60 * 60 * 1000), // 7 days from now
    framework: metadata.framework || null,
    frameworkName: metadata.frameworkName || null,
    messageCount: 0,
    messages: [],
    insights: [],
    sources: [],
    context: {},
    metadata: {
      timeSaved: 0,
      duration: 0,
      ...metadata.metadata
    }
  };

  // Save to storage
  const { sessions } = getAllSessions(companyIdSlug);
  sessions.unshift(session); // Add to beginning
  
  // Limit to last 50 sessions
  const limitedSessions = sessions.slice(0, 50);
  
  try {
    const key = getStorageKey(companyIdSlug);
    localStorage.setItem(key, JSON.stringify({
      sessions: limitedSessions,
      currentSessionId: sessionId
    }));
  } catch (e) {
    console.error('[SessionManager] Error creating session:', e);
    // If storage is full, try to remove oldest sessions
    if (e.name === 'QuotaExceededError') {
      const reduced = limitedSessions.slice(0, 25);
      try {
        localStorage.setItem(key, JSON.stringify({
          sessions: reduced,
          currentSessionId: sessionId
        }));
      } catch (e2) {
        console.error('[SessionManager] Failed to save even with reduced sessions:', e2);
      }
    }
  }

  return session;
};

/**
 * Get a specific session by ID
 */
export const getSession = (companyIdSlug, sessionId) => {
  const { sessions } = getAllSessions(companyIdSlug);
  return sessions.find(s => s.id === sessionId) || null;
};

/**
 * Get current active session
 */
export const getCurrentSession = (companyIdSlug) => {
  const { sessions, currentSessionId } = getAllSessions(companyIdSlug);
  if (!currentSessionId) return null;
  return sessions.find(s => s.id === currentSessionId) || null;
};

/**
 * Set current active session
 */
export const setCurrentSession = (companyIdSlug, sessionId) => {
  try {
    const key = getStorageKey(companyIdSlug);
    const data = getAllSessions(companyIdSlug);
    localStorage.setItem(key, JSON.stringify({
      ...data,
      currentSessionId: sessionId
    }));
  } catch (e) {
    console.error('[SessionManager] Error setting current session:', e);
  }
};

/**
 * Save/update a session
 */
export const saveSession = (companyIdSlug, sessionId, updates) => {
  try {
    const { sessions, currentSessionId } = getAllSessions(companyIdSlug);
    const index = sessions.findIndex(s => s.id === sessionId);
    
    if (index === -1) {
      // Session not found
      return null;
    }

    // Merge updates with existing session
    const updatedSession = {
      ...sessions[index],
      ...updates,
      updatedAt: Date.now()
    };

    sessions[index] = updatedSession;

    const key = getStorageKey(companyIdSlug);
    localStorage.setItem(key, JSON.stringify({
      sessions,
      currentSessionId
    }));

    return updatedSession;
  } catch (e) {
    console.error('[SessionManager] Error saving session:', e);
    return null;
  }
};

/**
 * Update session metadata (non-destructive merge)
 */
export const updateSession = (companyIdSlug, sessionId, updates) => {
  const session = getSession(companyIdSlug, sessionId);
  if (!session) return null;

  // Deep merge for nested objects
  const merged = {
    ...session,
    ...updates,
    metadata: {
      ...session.metadata,
      ...(updates.metadata || {})
    },
    context: {
      ...session.context,
      ...(updates.context || {})
    },
    updatedAt: Date.now()
  };

  return saveSession(companyIdSlug, sessionId, merged);
};

/**
 * Add message to session
 */
export const addMessageToSession = (companyIdSlug, sessionId, message) => {
  const session = getSession(companyIdSlug, sessionId);
  if (!session) return null;

  const updatedMessages = [...(session.messages || []), message];
  
  return updateSession(companyIdSlug, sessionId, {
    messages: updatedMessages,
    messageCount: updatedMessages.length
  });
};

/**
 * Add insight to session
 */
export const addInsightToSession = (companyIdSlug, sessionId, insight) => {
  const session = getSession(companyIdSlug, sessionId);
  if (!session) return null;

  const updatedInsights = [...(session.insights || []), insight];
  
  return updateSession(companyIdSlug, sessionId, {
    insights: updatedInsights
  });
};

/**
 * Update session context
 */
export const updateSessionContext = (companyIdSlug, sessionId, context) => {
  return updateSession(companyIdSlug, sessionId, {
    context: {
      ...getSession(companyIdSlug, sessionId)?.context,
      ...context
    }
  });
};

/**
 * List recent sessions
 */
export const listSessions = (companyIdSlug, limit = 10) => {
  const { sessions } = getAllSessions(companyIdSlug);
  // Sort by updatedAt descending (most recent first)
  const sorted = [...sessions].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
  return sorted.slice(0, limit);
};

/**
 * Delete a session
 */
export const deleteSession = (companyIdSlug, sessionId) => {
  try {
    const { sessions, currentSessionId } = getAllSessions(companyIdSlug);
    const filtered = sessions.filter(s => s.id !== sessionId);
    
    const key = getStorageKey(companyIdSlug);
    localStorage.setItem(key, JSON.stringify({
      sessions: filtered,
      currentSessionId: currentSessionId === sessionId ? null : currentSessionId
    }));

    return true;
  } catch (e) {
    console.error('[SessionManager] Error deleting session:', e);
    return false;
  }
};

/**
 * Clear all sessions (for testing/cleanup)
 */
export const clearAllSessions = (companyIdSlug) => {
  try {
    const key = getStorageKey(companyIdSlug);
    localStorage.removeItem(key);
    return true;
  } catch (e) {
    console.error('[SessionManager] Error clearing sessions:', e);
    return false;
  }
};
